import React, { useRef, useEffect, useState } from "react";
import "./CustomEditor.css";
import { SlList } from "react-icons/sl";
import {
  FaBold,
  FaItalic,
  FaUnderline,
  FaAlignLeft,
  FaAlignCenter,
  FaAlignRight,
  FaAlignJustify,
} from "react-icons/fa";
import { MdFormatListNumbered } from "react-icons/md";

const BoldIcon = FaBold as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const ItalicIcon = FaItalic as unknown as React.FC<
  React.SVGProps<SVGSVGElement>
>;
const UnderlineIcon = FaUnderline as unknown as React.FC<
  React.SVGProps<SVGSVGElement>
>;
const BulletListIcon = SlList as unknown as React.FC<
  React.SVGProps<SVGSVGElement>
>;
const NumberListIcon = MdFormatListNumbered as unknown as React.FC<
  React.SVGProps<SVGSVGElement>
>;
// const LinkIcon = SlLink as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
// const ImageIcon = SlPicture as unknown as React.FC<
//   React.SVGProps<SVGSVGElement>
// >;
const AlignLeftIcon = FaAlignLeft as unknown as React.FC<
  React.SVGProps<SVGSVGElement>
>;
const AlignCenterIcon = FaAlignCenter as unknown as React.FC<
  React.SVGProps<SVGSVGElement>
>;
const AlignRightIcon = FaAlignRight as unknown as React.FC<
  React.SVGProps<SVGSVGElement>
>;
const AlignJustifyIcon = FaAlignJustify as unknown as React.FC<
  React.SVGProps<SVGSVGElement>
>;

interface CustomEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  maxLength?: number;
}

const getPlainTextLength = (html: string) =>
  html
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim().length;

const CustomEditor: React.FC<CustomEditorProps> = ({
  value,
  onChange,
  placeholder = "Enter text here...",
  maxLength,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);

  // track whether we're editing source
  const [isSourceMode, setIsSourceMode] = useState(false);
  const [sourceValue, setSourceValue] = useState(value);

  // Keep track of the last value that originated from *this* editor
  const prevValueRef = useRef<string>(value);

  const selectionRef = useRef<Range | null>(null);
  const characterCount = getPlainTextLength(isSourceMode ? sourceValue : value);

  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      selectionRef.current = selection.getRangeAt(0);
    }
  };

  const restoreSelection = () => {
    const selection = window.getSelection();
    if (selection && selectionRef.current) {
      selection.removeAllRanges();
      selection.addRange(selectionRef.current);
    }
  };

  // --- INITIAL MOUNT: ensure editor DOM contains initial value ---
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = prevValueRef.current;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount

  // --- When parent sends a new `value` that is different from what this editor last wrote,
  //     treat it as an external update and apply it to editor and source textarea ---
  useEffect(() => {
    if (value !== prevValueRef.current) {
      prevValueRef.current = value;
      setSourceValue(value);
      // only overwrite DOM if not in source mode (so we don't clobber live source editing)
      if (!isSourceMode && editorRef.current) {
        editorRef.current.innerHTML = value;
      }
    }
  }, [value, isSourceMode]);

  // --- When toggling back from source-mode to normal mode, apply sourceValue into DOM ---
  useEffect(() => {
    if (!isSourceMode && editorRef.current) {
      editorRef.current.innerHTML = sourceValue;
      prevValueRef.current = sourceValue;
    }
  }, [isSourceMode, sourceValue]);

  // Image resizing handlers — update prevValueRef before calling onChange
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const handleImageClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === "IMG") {
        const img = target as HTMLImageElement;

        const currentWidth =
          img.style.width?.replace("px", "") ||
          img.getAttribute("width") ||
          img.naturalWidth.toString();

        const currentHeight =
          img.style.height?.replace("px", "") ||
          img.getAttribute("height") ||
          img.naturalHeight.toString();

        const newWidth = prompt(
          "Enter image width in pixels:",
          currentWidth || "300",
        );
        if (!newWidth) return;

        const newHeight = prompt(
          "Enter image height in pixels (optional):",
          currentHeight || "",
        );

        img.removeAttribute("width");
        img.removeAttribute("height");
        img.style.width = `${parseInt(newWidth, 10)}px`;
        img.style.height = newHeight ? `${parseInt(newHeight, 10)}px` : "auto";

        img.setAttribute("data-fixed-width", img.style.width);
        img.setAttribute("data-fixed-height", img.style.height);

        // mark as internal change before notifying parent
        const newHtml = editor.innerHTML;
        prevValueRef.current = newHtml;
        onChange(newHtml);
      }
    };

    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === "IMG") {
        const img = target as HTMLImageElement;
        const fixedWidth = img.getAttribute("data-fixed-width");
        const fixedHeight = img.getAttribute("data-fixed-height");
        if (fixedWidth) img.style.width = fixedWidth;
        if (fixedHeight) img.style.height = fixedHeight;
      }
    };

    editor.addEventListener("click", handleImageClick);
    editor.addEventListener("mousedown", handleMouseDown);
    return () => {
      editor.removeEventListener("click", handleImageClick);
      editor.removeEventListener("mousedown", handleMouseDown);
    };
  }, [onChange]);

  const placeCaretAtEnd = () => {
    const editor = editorRef.current;
    if (!editor) return;

    editor.focus();

    const range = document.createRange();
    range.selectNodeContents(editor);
    range.collapse(false); // move to end

    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  const exec = (command: string, valueArg?: string) => {
    const selection = window.getSelection();

    // ❗ If no selection or editor not focused → move cursor to end
    if (!selection || selection.rangeCount === 0) {
      placeCaretAtEnd();
    } else {
      restoreSelection();
    }

    document.execCommand(command, false, valueArg);

    if (editorRef.current) {
      const newHtml = editorRef.current.innerHTML;

      if (maxLength && getPlainTextLength(newHtml) > maxLength) {
        editorRef.current.innerHTML = prevValueRef.current;
        return;
      }

      prevValueRef.current = newHtml;
      onChange(newHtml);
    }
  };

  // input handler for contentEditable — mark prevValueRef and call onChange
  const handleInput = () => {
    if (editorRef.current) {
      let newHtml = editorRef.current.innerHTML;

      // ❗ Normalize empty editor content
      if (
        newHtml === "<br>" ||
        newHtml === "<div><br></div>" ||
        newHtml === "<p><br></p>"
      ) {
        newHtml = "";
        editorRef.current.innerHTML = "";
      }

      if (maxLength && getPlainTextLength(newHtml) > maxLength) {
        editorRef.current.innerHTML = prevValueRef.current;
        placeCaretAtEnd();
        return;
      }

      prevValueRef.current = newHtml;
      onChange(newHtml);
    }
  };

  // source textarea change (edit raw HTML) — mark prevValueRef before notifying parent
  const handleSourceChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;

    if (maxLength && getPlainTextLength(newValue) > maxLength) {
      return;
    }

    setSourceValue(newValue);
    prevValueRef.current = newValue;
    onChange(newValue);
  };

  return (
    <div className="custom-editor-container">
      <div className="toolbar">
        <select
          onChange={(e) => exec("formatBlock", e.target.value)}
          defaultValue=""
        >
          <option value="" disabled>
            Font Style
          </option>
          <option value="p">Paragraph</option>
          <option value="h1">H1</option>
          <option value="h2">H2</option>
          <option value="h3">H3</option>
        </select>

        <select
          onChange={(e) => exec("fontSize", e.target.value)}
          defaultValue=""
        >
          <option value="" disabled>
            Size
          </option>
          {[1, 2, 3, 4, 5, 6, 7].map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        <input
          type="color"
          title="Text Color"
          onChange={(e) => exec("foreColor", e.target.value)}
          style={{ marginRight: "10px", cursor: "pointer" }}
        />

        <button type="button" onClick={() => exec("bold")} title="Bold">
          <BoldIcon />
        </button>
        <button type="button" onClick={() => exec("italic")} title="Italic">
          <ItalicIcon />
        </button>
        <button
          type="button"
          onClick={() => exec("underline")}
          title="Underline"
        >
          <UnderlineIcon />
        </button>

        <button
          type="button"
          onClick={() => exec("insertUnorderedList")}
          title="Bullet List"
        >
          <BulletListIcon />
        </button>
        <button
          type="button"
          onClick={() => exec("insertOrderedList")}
          title="Numbered List"
        >
          <NumberListIcon />
        </button>

        <button
          type="button"
          onClick={() => exec("justifyLeft")}
          title="Align Left"
        >
          <AlignLeftIcon />
        </button>
        <button
          type="button"
          onClick={() => exec("justifyCenter")}
          title="Align Center"
        >
          <AlignCenterIcon />
        </button>
        <button
          type="button"
          onClick={() => exec("justifyRight")}
          title="Align Right"
        >
          <AlignRightIcon />
        </button>
        <button
          type="button"
          onClick={() => exec("justifyFull")}
          title="Justify"
        >
          <AlignJustifyIcon />
        </button>

        {/* <button
          type="button"
          onClick={() => {
            const url = prompt("Enter link URL:");
            if (!url) return;

            const editor = editorRef.current;
            if (!editor) return;

            editor.focus();

            const selection = window.getSelection();

            // ❗ If nothing is selected → insert text first, then link it
            if (
              !selection ||
              selection.rangeCount === 0 ||
              selection.isCollapsed
            ) {
              document.execCommand("insertText", false, url);

              const range = document.createRange();
              range.selectNodeContents(editor.lastChild!);
              selection?.removeAllRanges();
              selection?.addRange(range);
            }

            exec("createLink", url);
          }}
          title="Insert Link"
        >
          <LinkIcon />
        </button>
        <button
          type="button"
          onClick={() => {
            const url = prompt("Enter image URL:");
            if (url) exec("insertImage", url);
          }}
          title="Insert Image"
        >
          <ImageIcon />
        </button> */}

        <button
          type="button"
          onClick={() => setIsSourceMode((s) => !s)}
          title="Toggle Source Code"
          style={{ marginLeft: "10px" }}
        >
          {"</>"}
        </button>
      </div>

      {!isSourceMode ? (
        <div
          ref={editorRef}
          className="editor"
          contentEditable
          onInput={handleInput}
          onMouseUp={saveSelection}
          onKeyUp={saveSelection}
          data-placeholder={placeholder}
          suppressContentEditableWarning
        />
      ) : (
        <textarea
          className="editor source-editor"
          value={sourceValue}
          onChange={handleSourceChange}
          style={{
            width: "100%",
            height: "300px",
            padding: "10px",
            fontFamily: "monospace",
            fontSize: "14px",
          }}
        />
      )}

      {maxLength && (
        <div className="custom-editor-count">
          {characterCount}/{maxLength}
        </div>
      )}
    </div>
  );
};

export default CustomEditor;
